import { useEffect, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import Select from "react-select";
import { IoMdClose } from "react-icons/io";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import FixedSave from "../../../../components/Actions/FixedSave";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { get, post } from "../../../../utils/helpers/api";
import { toast } from "react-toastify";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import { useTranslation } from "react-i18next";
import CustomSelect from "../../../../components/Forms/CustomSelect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { useSettings } from "../../../../context/settings";
import WarehouseSelect from "../../../../components/Forms/WarehouseSelect";
import DateTimeInput from "../../../../components/Forms/DateTimeInput";
import DefaultButton from "../../../../components/Buttons/Default";
import TaxModal from "../../../Sales/pages/Quotes/Modals/Tax";
import DiscountModal from "../../../Sales/pages/Quotes/Modals/Discount";

function CreateOrderPage() {
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [initLoading, setInitLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [suppliersData, setSuppliersData] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [orderData, setOrderData] = useState({
    order_number: state?.orderData?.order_number || "",
    supplier_id: state?.orderData?.supplier_id || "",
    order_date: state?.orderData?.order_date ? parseISO(state.orderData.order_date) : new Date(),
    warehouse_id: state?.orderData?.warehouse_id || "",
    expected_delivery_date: state?.orderData?.expected_delivery_date ? parseISO(state.orderData.expected_delivery_date) : null,
    status: state?.orderData?.status || "draft",
    tax_rate: state?.orderData?.tax_rate || 0,
    discount_rate: state?.orderData?.discount_rate || 0,
    discount_type: state?.orderData?.discount_type || "percent",
    notes: state?.orderData?.notes || "",
  });

  const [rows, setRows] = useState(
    state?.orderData?.items?.length > 0
      ? state.orderData.items.map((item, index) => ({
          id: Date.now() + index,
          product_name: item.product_name || '',
          product_id: item.product_id || null,
          description: item.description || '',
          quantity: item.quantity || 1,
          unitPrice: item.unit_price || 0,
          total: item.total_price || 0,
        }))
      : [{ id: Date.now(), product_name: '', product_id: null, description: '', quantity: 1, unitPrice: 0, total: 0 }]
  );

  useEffect(() => {
    initData();
  }, []);

  useEffect(() => {
    if (id && !state?.orderData) {
      getOrder();
    }
  }, [id]);

  const initData = async () => {
    setInitLoading(true);
    const data = await get('/purchases/orders/create');
    if (data.success) {
      setSuppliersData(data.suppliers);
      setCompanySettings(data.company_settings || {});
      if (!orderData.order_number) {
        setOrderData(prev => ({ ...prev, order_number: data.order_number }));
      }
    }
    setInitLoading(false);
  };

  const getOrder = async () => {
    const data = await get('/purchases/orders/view/' + id);
    if (data?.data) {
      setOrderData({
        ...data.data,
        order_date: data.data.order_date ? parseISO(data.data.order_date) : new Date(),
        expected_delivery_date: data.data.expected_delivery_date ? parseISO(data.data.expected_delivery_date) : null,
      });
      if (data.data.items?.length > 0) {
        setRows(data.data.items.map((item, index) => ({
          id: Date.now() + index,
          product_name: item.product_name || '',
          product_id: item.product_id || null,
          description: item.description || '',
          quantity: item.quantity || 1,
          unitPrice: item.unit_price || 0,
          total: item.total_price || 0,
        })));
      }
    }
  };

  const handleAddRow = () => {
    setRows([...rows, { id: Date.now(), product_name: '', product_id: null, description: '', quantity: 1, unitPrice: 0, total: 0 }]);
  };

  const handleDeleteRow = (rowId) => {
    setRows(rows.filter(row => row.id !== rowId));
  };

  const handleRowChange = (rowId, field, value) => {
    setRows(rows.map(r => {
      if (r.id !== rowId) return r;
      const updated = { ...r, [field]: value };
      if (field === 'quantity' || field === 'unitPrice') {
        updated.total = parseFloat(updated.quantity || 0) * parseFloat(updated.unitPrice || 0);
      }
      return updated;
    }));
  };

  const subTotal = rows.reduce((acc, row) => acc + parseFloat(row.total || 0), 0);

  const saveOrder = async () => {
    if (!orderData.supplier_id) {
      toast.error(t('purchases.selectSupplierRequired'));
      return;
    }
    if (!orderData.order_date) {
      toast.error(t('purchases.orderDateRequired'));
      return;
    }
    setSaveLoading(true);
    const url = id ? '/purchases/orders/update/' + id : '/purchases/orders/store';
    const data = await post(url, {
      ...orderData,
      order_date: orderData.order_date ? format(orderData.order_date, 'yyyy-MM-dd') : null,
      expected_delivery_date: orderData.expected_delivery_date ? format(orderData.expected_delivery_date, 'yyyy-MM-dd') : null,
      items: rows
    });
    if (data.success) {
      navigate('/purchases/purchase-orders');
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  const supplierOptions = suppliersData.map(s => ({
    value: s.id,
    label: s.company_name || [s.firstname, s.lastname].filter(Boolean).join(' ') || s.id
  }));

  const calcPrices = () => {
    const taxRate = orderData.tax_rate || 0;
    const discountRate = orderData.discount_rate || 0;
    const subTotal = rows.reduce((acc, row) => acc + row.total, 0);
    const discountAmount = function() {
      if(orderData.discount_type === 'percent') {
        return subTotal * discountRate / 100;
      } else {
        return discountRate;
      }
    };
    const taxAmount = (subTotal - discountAmount()) * taxRate / 100;
    const total = subTotal - discountAmount() + taxAmount;

    return {
      subTotal, 
      discountAmount: discountAmount(),
      taxAmount, 
      total 
    };
  }

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('purchases.orders'), id ? t('common.edit') : t('purchases.newOrder')]}
          onBack={() => navigate('/purchases/purchase-orders')}
        />
        <div className="subheader-page-content scrollable">
          <Row>
            <Col md="6" className="mb-3">
              <Label>{t("purchases.supplier")}</Label>
              <Select
                options={supplierOptions}
                styles={SelectCustomStyles}
                isLoading={initLoading}
                value={supplierOptions.find(o => o.value === orderData.supplier_id) || null}
                onChange={selected => setOrderData({ ...orderData, supplier_id: selected ? selected.value : '' })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t("purchases.orderNumber")}</Label>
              <Input
                value={orderData.order_number}
                readOnly
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t("purchases.orderDate")}</Label>
              <DateTimeInput
                selected={orderData.order_date}
                onChange={(d) => setOrderData({ ...orderData, order_date: d })}
              />
            </Col>
            <Col md="3" className="mb-3">
              <Label>{t("purchases.expectedDelivery")}</Label>
              <DatePicker
                selected={orderData.expected_delivery_date}
                onChange={(d) => setOrderData({ ...orderData, expected_delivery_date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>
            <Col md="3" className="mb-3">
              {/* Where the goods land when the order is marked received. */}
              <Label>{t("warehouses.receiveInto")}</Label>
              <WarehouseSelect
                value={orderData.warehouse_id}
                onChange={(v) => setOrderData({ ...orderData, warehouse_id: v })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t("common.status")}</Label>
              <Input
                type="select"
                value={orderData.status}
                onChange={e => setOrderData({ ...orderData, status: e.target.value })}
              >
                <option value="draft">{t("common.draft")}</option>
                <option value="sent">{t("common.sent")}</option>
                <option value="approved">{t("common.approved")}</option>
                <option value="received">{t("common.received")}</option>
                <option value="canceled">{t("common.canceled")}</option>
              </Input>
            </Col>
          </Row>

          <Table bordered className="mt-2">
            <thead>
              <tr>
                <th>{t('common.item')}</th>
                <th width="240px">{t('common.description')}</th>
                <th width="110px">{t('common.qty')}</th>
                <th width="120px">{t('common.unitPrice')}</th>
                <th width="130px">{t('common.total')}</th>
                <th width="40px"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <CustomSelect
                      value={row.product_name ? { value: row.product_id, label: row.product_name } : null}
                      onChange={(selected, objectData) => {
                        setRows(prev => prev.map(r => {
                          if (r.id !== row.id) return r;
                          const unitPrice = objectData?.cost_price ?? objectData?.price ?? r.unitPrice;
                          return {
                            ...r,
                            product_id: selected ? selected.value : null,
                            product_name: selected ? selected.label : '',
                            unitPrice,
                            total: parseFloat(r.quantity || 0) * parseFloat(unitPrice || 0) * (1 - parseFloat(r.discountRate || 0) / 100)
                          };
                        }));
                      }}
                      module="inventory/products"
                      placeholder={t('inventory.searchProduct')}
                      createLink="/inventory/products/create"
                    />
                  </td>
                  <td>
                    <Input
                      type="textarea"
                      rows="1"
                      value={row.description}
                      onChange={e => handleRowChange(row.id, 'description', e.target.value)}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      min="1"
                      value={row.quantity}
                      onChange={e => handleRowChange(row.id, 'quantity', e.target.value)}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.unitPrice}
                      onChange={e => handleRowChange(row.id, 'unitPrice', e.target.value)}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={parseFloat(row.total || 0).toFixed(2)}
                      readOnly
                    />
                  </td>
                  <td className="text-center align-middle">
                    <IoMdClose
                      size={18}
                      style={{ cursor: 'pointer', color: '#dc3545' }}
                      onClick={() => handleDeleteRow(row.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Row>
            <Col lg="7" md="6">
              <Row className="mb-3 g-2">
                <Col md="4">              
                  <DefaultButton 
                    color="primary" 
                    outline size="sm" 
                    onClick={handleAddRow}
                    block
                  >
                    + {t('common.addNewRow')}
                  </DefaultButton>
                </Col>
                <Col md="4">
                  <DefaultButton
                    color="success"
                    outline
                    size="sm"
                    block
                    onClick={() => setDiscountModalOpen(true)}
                  >
                    + {t('sales.addDiscount')}
                  </DefaultButton>
                </Col>
                <Col md="4">
                  <DefaultButton
                    color="light"
                    outline
                    size="sm"
                    block
                    onClick={() => setTaxModalOpen(true)}
                  >
                    + {t('sales.editTax')}
                  </DefaultButton>
                </Col>
              </Row>
              <FormGroup>
                <Label>{t('common.notes')}: </Label>
                <Input
                  type="textarea"
                  rows="4"
                  value={orderData.notes}
                  onChange={e => setOrderData({ ...orderData, notes: e.target.value })}
                />
              </FormGroup>
            </Col>

            <Col lg="5" md="6">
              <div className="bg-white p-3 border rounded">
                <Row className="mb-2">
                  <Col md="6"><strong>{t('common.subTotal')}:</strong></Col>
                  <Col md="6" className="text-end">{subTotal.toFixed(2)} { companySettings?.sales?.base_currency }</Col>
                </Row>

                {orderData.discount_rate > 0 &&
                  <Row className="mb-2 text-danger">
                    <Col md="6">
                      <strong>
                        {t('sales.discountWithRate')}: 
                        ({ orderData.discount_rate }
                        { orderData?.discount_type === 'percent' ? '%' : '' })
                      </strong>
                    </Col>
                    <Col md="6" className="text-end">
                      -{ calcPrices().discountAmount.toFixed(2) } {' '}
                      { companySettings?.sales?.base_currency }
                    </Col>
                  </Row>
                }

                <Row className="mb-3">
                  <Col md="6">
                    <strong>{t('sales.taxWithRate', { rate: orderData.tax_rate })}: </strong>
                  </Col>
                  <Col md="6" className="text-end">
                    { calcPrices().taxAmount.toFixed(2) } {' '}
                    { companySettings?.sales?.base_currency }
                  </Col>
                </Row>

                <Row>
                  <Col md="6"><h5 className="fw-bold mb-0">{t('common.total')}:</h5></Col>
                  <Col md="6" className="text-end"><h5 className="fw-bold mb-0">{calcPrices().total.toFixed(2)} { companySettings?.sales?.base_currency }</h5></Col>
                </Row>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <TaxModal 
        isOpen={taxModalOpen} 
        toggle={() => setTaxModalOpen(false)} 
        taxRate={orderData.tax_rate}
        onTaxRateChange={(newTaxRate) => setOrderData(prev => ({ ...prev, tax_rate: newTaxRate }))}
      />

      <DiscountModal
        isOpen={discountModalOpen} 
        toggle={() => setDiscountModalOpen(false)} 
        discountRate={orderData.discount_rate}
        onDiscountRateChange={(newDiscountRate) => setOrderData(prev => ({ ...prev, discount_rate: newDiscountRate }))}
        discountType={orderData.discount_type}
        onDiscountTypeChange={(newDiscountType) => setOrderData(prev => ({ ...prev, discount_type: newDiscountType }))}
      />

      <FixedSave
        onSave={saveOrder}
        loading={saveLoading}
        disabled={saveLoading || initLoading}
      />
    </SidebarLayout>
  );
}

export default CreateOrderPage;
